import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2 } from "lucide-react";

export default function Notices() {
  const { user } = useAuth();
  const canEdit = user.role === "admin";

  const [notices, setNotices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agent_id: "", message: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status !== "all") params.status = status;
      const { data } = await api.get("/notices", { params });
      setNotices(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/users/agents").then(({ data }) => setAgents(data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.agent_id) return toast.error("Select an agent");
    if (!form.message.trim()) return toast.error("Enter a message");
    try {
      await api.post("/notices", form);
      toast.success("Notice issued");
      setOpen(false);
      setForm({ agent_id: "", message: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolve = async (id) => {
    try { await api.put(`/notices/${id}/resolve`); toast.success("Resolved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try { await api.delete(`/notices/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">BDM Notices</h1>
          <p className="text-sm text-muted-foreground">Blocking pop-ups for sales BDMs. They cannot dismiss them.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} data-testid="add-notice-btn">
            <Plus className="w-4 h-4 mr-2" /> Issue notice
          </Button>
        )}
      </div>

      <Card className="p-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="max-w-xs" data-testid="notices-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>BDM</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Proof</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Resolved</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notices.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No notices</TableCell></TableRow>
              )}
              {notices.map((n) => (
                <TableRow key={n.id} data-testid={`notice-row-${n.id}`}>
                  <TableCell className="font-medium">{n.agent_name}</TableCell>
                  <TableCell className="max-w-[280px] whitespace-normal break-words text-sm">{n.message}</TableCell>
                  <TableCell>
                    <StatusBadge status={n.status === "active" ? "Pending" : "Approved"} />
                  </TableCell>
                  <TableCell>
                    {n.proof_upload
                      ? <a href={`/api/files/${n.proof_upload}`} target="_blank" rel="noreferrer" className="text-xs underline text-blue-600">View proof</a>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {n.resolved_at ? new Date(n.resolved_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit && n.status === "active" && (
                      <Button variant="ghost" size="sm" onClick={() => resolve(n.id)} data-testid={`resolve-notice-${n.id}`}>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => del(n.id)} data-testid={`delete-notice-${n.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Issue blocking notice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sales BDM</Label>
                <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
                  <SelectTrigger data-testid="notice-agent-select"><SelectValue placeholder="Select BDM" /></SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  rows={5}
                  placeholder="e.g. ₹5000 payment is still pending. Upload UTR proof to continue."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="notice-message-input"
                />
                <p className="text-xs text-muted-foreground">The BDM will not be able to dismiss this until you mark it resolved.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-notice-btn">Issue notice</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
